/**
 * @fileoverview Dashboard page component for authenticated users.
 * Displays aggregate monitor statistics, lists all user monitors with
 * quick actions (view, toggle, delete), and provides navigation to
 * create new monitors. Automatically redirects to login if unauthenticated.
 * @module pages/dashboard
 */

import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth.js";
import { useToast } from "@/hooks/use-toast";
import { isUnauthorizedError } from "@/lib/authUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, Plus, ExternalLink, Trash2, Pause, Play } from "lucide-react";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Monitor } from "@shared/schema";

/**
 * Dashboard page component.
 * Shows monitor statistics cards (total, active, uptime, alerts) and
 * a grid of monitor cards with management controls. Fetches data via
 * TanStack Query and handles mutations for toggle/delete actions.
 * @returns Dashboard page JSX
 */
export default function Dashboard() {
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  const { data: monitors, isLoading: monitorsLoading } = useQuery<Monitor[]>({
    queryKey: ["/api/monitors"],
    enabled: isAuthenticated,
  });

  const { data: stats } = useQuery<{
    totalMonitors: number;
    activeMonitors: number;
    avgUptime: number;
    recentAlerts: number;
  }>({
    queryKey: ["/api/monitors/stats"],
    enabled: isAuthenticated,
  });

  const toggleMonitorMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      await apiRequest("PATCH", `/api/monitors/${id}`, { isActive: !isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitors/stats"] });
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to update monitor",
        variant: "destructive",
      });
    },
  });

  const deleteMonitorMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/monitors/${id}`, undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitors/stats"] });
      toast({
        title: "Success",
        description: "Monitor deleted successfully",
      });
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to delete monitor",
        variant: "destructive",
      });
    },
  });

  if (authLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 bg-background z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link href="/" data-testid="link-home" className="flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            <span className="text-lg font-semibold">Clockwork</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/new" data-testid="link-create-monitor" asChild>
              <Button data-testid="button-create-monitor">
                <Plus className="h-4 w-4 mr-2" />
                Create Monitor
              </Button>
            </Link>
            <Button
              variant="ghost"
              data-testid="button-logout"
              onClick={async () => {
                try {
                  setIsLoggingOut(true);
                  await logout();
                } catch (error) {
                  toast({
                    title: "Error",
                    description: "Failed to log out",
                    variant: "destructive",
                  });
                  setIsLoggingOut(false);
                }
              }}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? "Logging out..." : "Log Out"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-main-content">
        <div className="grid gap-4 md:grid-cols-4 mb-8" data-testid="dashboard-stats-grid">
          <Card data-testid="dashboard-stat-total-card">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2" data-testid="dashboard-stat-total-header">
              <h3 className="text-sm font-medium text-muted-foreground" data-testid="dashboard-stat-total-title">Total Monitors</h3>
            </CardHeader>
            <CardContent data-testid="dashboard-stat-total-content">
              {stats ? (
                <div className="text-2xl font-semibold" data-testid="dashboard-stat-total-value">
                  {stats.totalMonitors}
                </div>
              ) : (
                <Skeleton className="h-8 w-16" data-testid="dashboard-stat-total-skeleton" />
              )}
            </CardContent>
          </Card>

          <Card data-testid="dashboard-stat-active-card">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2" data-testid="dashboard-stat-active-header">
              <h3 className="text-sm font-medium text-muted-foreground" data-testid="dashboard-stat-active-title">Active</h3>
            </CardHeader>
            <CardContent data-testid="dashboard-stat-active-content">
              {stats ? (
                <div className="text-2xl font-semibold" data-testid="dashboard-stat-active-value">
                  {stats.activeMonitors}
                </div>
              ) : (
                <Skeleton className="h-8 w-16" data-testid="dashboard-stat-active-skeleton" />
              )}
            </CardContent>
          </Card>

          <Card data-testid="dashboard-stat-uptime-card">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2" data-testid="dashboard-stat-uptime-header">
              <h3 className="text-sm font-medium text-muted-foreground" data-testid="dashboard-stat-uptime-title">Avg Uptime</h3>
            </CardHeader>
            <CardContent data-testid="dashboard-stat-uptime-content">
              {stats ? (
                <div className="text-2xl font-semibold" data-testid="dashboard-stat-uptime-value">
                  {stats.avgUptime.toFixed(1)}%
                </div>
              ) : (
                <Skeleton className="h-8 w-16" data-testid="dashboard-stat-uptime-skeleton" />
              )}
            </CardContent>
          </Card>

          <Card data-testid="dashboard-stat-alerts-card">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2" data-testid="dashboard-stat-alerts-header">
              <h3 className="text-sm font-medium text-muted-foreground" data-testid="dashboard-stat-alerts-title">Recent Alerts</h3>
            </CardHeader>
            <CardContent data-testid="dashboard-stat-alerts-content">
              {stats ? (
                <div className="text-2xl font-semibold" data-testid="dashboard-stat-alerts-value">
                  {stats.recentAlerts}
                </div>
              ) : (
                <Skeleton className="h-8 w-16" data-testid="dashboard-stat-alerts-skeleton" />
              )}
            </CardContent>
          </Card>
        </div>

        <div className="mb-6">
          <h2 className="text-2xl font-semibold mb-2">Your Monitors</h2>
          <p className="text-sm text-muted-foreground">
            Manage and track your API endpoints
          </p>
        </div>

        {monitorsLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-monitors-loading">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : monitors && monitors.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-monitors-grid">
            {monitors.map((monitor) => (
              <Card key={monitor.id} className="hover-elevate" data-testid={`dashboard-monitor-card-${monitor.id}`}>
                <CardHeader className="space-y-0 pb-4" data-testid={`dashboard-monitor-card-${monitor.id}-header`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-base" data-testid={`dashboard-monitor-card-${monitor.id}-title`}>
                      {monitor.name}
                    </h3>
                    <Badge variant={monitor.isActive ? "default" : "secondary"} data-testid={`dashboard-monitor-card-${monitor.id}-status-badge`}>
                      {monitor.isActive ? "Active" : "Paused"}
                    </Badge>
                  </div>
                  <p className="text-sm font-mono text-muted-foreground break-all" data-testid={`dashboard-monitor-card-${monitor.id}-url`}>
                    {monitor.url}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3" data-testid={`dashboard-monitor-card-${monitor.id}-content`}>
                  <div className="flex items-center justify-between text-xs" data-testid={`dashboard-monitor-card-${monitor.id}-interval-row`}>
                    <span className="text-muted-foreground">Interval</span>
                    <span className="font-medium" data-testid={`dashboard-monitor-card-${monitor.id}-interval`}>
                      {monitor.interval < 60000
                        ? `${Math.floor(monitor.interval / 1000)}s`
                        : monitor.interval < 3600000
                          ? `${Math.floor(monitor.interval / 60000)}m`
                          : `${Math.floor(monitor.interval / 3600000)}h`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs" data-testid={`dashboard-monitor-card-${monitor.id}-method-row`}>
                    <span className="text-muted-foreground">Method</span>
                    <span className="font-medium font-mono" data-testid={`dashboard-monitor-card-${monitor.id}-method`}>
                      {monitor.method}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pt-2" data-testid={`dashboard-monitor-card-${monitor.id}-actions`}>
                    <Link href={`/monitor/${monitor.id}`} data-testid={`dashboard-monitor-card-${monitor.id}-view-link`} asChild>
                      <Button variant="outline" size="sm" className="w-full" data-testid={`dashboard-monitor-card-${monitor.id}-view-button`}>
                        <ExternalLink className="h-3 w-3 mr-1" data-testid={`dashboard-monitor-card-${monitor.id}-view-icon`} />
                        View
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleMonitorMutation.mutate({ id: monitor.id, isActive: monitor.isActive })}
                      disabled={toggleMonitorMutation.isPending}
                      data-testid={`dashboard-monitor-card-${monitor.id}-toggle-button`}
                    >
                      {monitor.isActive ? (
                        <Pause className="h-3 w-3" data-testid={`dashboard-monitor-card-${monitor.id}-pause-icon`} />
                      ) : (
                        <Play className="h-3 w-3" data-testid={`dashboard-monitor-card-${monitor.id}-play-icon`} />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Are you sure you want to delete this monitor?")) {
                          deleteMonitorMutation.mutate(monitor.id);
                        }
                      }}
                      disabled={deleteMonitorMutation.isPending}
                      data-testid={`dashboard-monitor-card-${monitor.id}-delete-button`}
                    >
                      <Trash2 className="h-3 w-3" data-testid={`dashboard-monitor-card-${monitor.id}-delete-icon`} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12" data-testid="dashboard-empty-state">
            <div className="text-center">
              <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2" data-testid="text-no-monitors">No monitors yet</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Create your first monitor to start tracking your APIs
              </p>
              <Link href="/new" data-testid="link-create-first-monitor" asChild>
                <Button data-testid="button-create-first-monitor">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Monitor
                </Button>
              </Link>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
